import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { supabase } from "./supabaseClient";
import logo from "./assets/logo.png";
import "./styles/AuthModal.css";

const AuthModalContext = createContext({
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

const EMAIL_CONFIRM_REDIRECT_URL = "https://snowsproam.com/auth/callback";

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentGrad: "linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.18)",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.10)",
  accentBorder: "rgba(12,105,200,0.16)",
  bgCard: "#FFFFFF",
  bgAlt: "#F5F7FA",
  bgElevated: "#EDF0F5",
  border: "rgba(10,29,68,0.10)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  danger: "#DC2626",
  inputBg: "#F5F7FA",
  inputBorder: "rgba(10,29,68,0.14)",
  inputText: "#0F1629",
};

const IRELAND_REGIONS = ["Carlow", "Cavan", "Clare", "Cork", "Donegal", "Dublin", "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath", "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Waterford", "Westmeath", "Wexford", "Wicklow", "Antrim", "Armagh", "Derry", "Down", "Fermanagh", "Tyrone"];
const UK_REGIONS = ["Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "Durham", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire"];

const isEmailNotConfirmedError = (message) => {
  const normalized = String(message || "").toLowerCase();
  return normalized.includes("email not confirmed") || normalized.includes("confirm your email") || normalized.includes("not confirmed");
};

const safeLocalRedirect = (value) => {
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "";
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return "";
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return "";
  }
};

const fieldStyle = (error, C) => ({
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: `1px solid ${error ? "rgba(248,113,113,0.50)" : C.inputBorder}`,
  background: C.inputBg,
  color: C.inputText,
  fontSize: 16,
  fontFamily: "'DM Sans',sans-serif",
  outline: "none",
  boxShadow: error ? "0 0 0 3px rgba(248,113,113,0.10)" : "none",
});

function Field({ label, type = "text", value, onChange, error, errorMsg, autoComplete, name, C }) {
  const errorId = useId();
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span id={`${errorId}-label`} style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>{label}</span>
      <input aria-labelledby={`${errorId}-label`} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} type={type} name={name} value={value} onChange={onChange} autoComplete={autoComplete} style={fieldStyle(error, C)} />
      {error && <span id={errorId} style={{ fontSize: 12, color: C.danger, fontWeight: 700 }}>{errorMsg}</span>}
    </label>
  );
}

function PasswordField({ label, value, onChange, show, toggle, error, errorMsg, autoComplete, name, C }) {
  const errorId = useId();
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span id={`${errorId}-label`} style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>{label}</span>
      <span style={{ position: "relative", display: "block" }}>
        <input aria-labelledby={`${errorId}-label`} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} type={show ? "text" : "password"} name={name} value={value} onChange={onChange} autoComplete={autoComplete} style={{ ...fieldStyle(error, C), paddingRight: 42 }} />
        <button type="button" onClick={toggle} aria-label={show ? "Hide password" : "Show password"} style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", width: 30, height: 30, border: "none", background: "transparent", color: C.textMuted, cursor: "pointer" }}>
          <FontAwesomeIcon icon={show ? faEyeSlash : faEye} />
        </button>
      </span>
      {error && <span id={errorId} style={{ fontSize: 12, color: C.danger, fontWeight: 700 }}>{errorMsg}</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, error, disabled, options, C }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>{label}</span>
      <select value={value} onChange={onChange} disabled={disabled} style={{ ...fieldStyle(error, C), cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}>
        {options.map((option) => <option key={option.v} value={option.v}>{option.l}</option>)}
      </select>
      {error && <span style={{ fontSize: 12, color: C.danger, fontWeight: 700 }}>Required</span>}
    </label>
  );
}

export function GlobalAuthProvider({ children }) {
  const navigate = useNavigate();
  const C = LIGHT;
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("signin");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [unconfirmedSignIn, setUnconfirmedSignIn] = useState(false);
  const [postAuthRedirect, setPostAuthRedirect] = useState("");
  const [authPrompt, setAuthPrompt] = useState({ title: "", body: "" });
  const [signInData, setSignInData] = useState({ email: "", password: "" });
  const [signInErrors, setSignInErrors] = useState({});
  const [signup, setSignup] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    businessName: "",
    instituteType: "",
    groupSize: "",
    country: "",
    region: "",
    agreeTerms: false,
  });
  const [signupErrors, setSignupErrors] = useState({});

  const closeAuthModal = useCallback(() => {
    setOpen(false);
    setError("");
    setBusy(false);
    setPostAuthRedirect("");
    setAuthPrompt({ title: "", body: "" });
  }, []);

  const openAuthModal = useCallback((options = {}) => {
    setMode("signin");
    setError("");
    setSignInErrors({});
    setSignupErrors({});
    setUnconfirmedSignIn(false);
    setPostAuthRedirect(safeLocalRedirect(options.redirectTo));
    setAuthPrompt({
      title: String(options.title || "").trim(),
      body: String(options.body || "").trim(),
    });
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    dialog.querySelector('input[name="email"]')?.focus();
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [closeAuthModal, open]);

  const updateSignup = (field, value) => {
    setSignup((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "instituteType") {
        next.groupSize = value === "School" || value === "College" ? "fixed" : "";
      }
      if (field === "country") next.region = "";
      return next;
    });
    setSignupErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleForgotPassword = async () => {
    if (!signInData.email.trim()) {
      toast.error("Enter your email for a reset link.");
      return;
    }
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(signInData.email.trim(), { redirectTo: `${window.location.origin}/reset-password` });
      if (resetError) throw resetError;
      toast.success("Reset email sent. Check your inbox.");
    } catch (err) {
      toast.error(err?.message || "Could not send reset email.");
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    if (busy) return;
    const nextErrors = {
      email: !signInData.email.trim(),
      password: !signInData.password.trim(),
    };
    setSignInErrors(nextErrors);
    if (nextErrors.email || nextErrors.password) {
      setError("Please fill in all fields.");

      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: signInData.email.trim(),
        password: signInData.password,
      });
      if (signInError) {
        if (isEmailNotConfirmedError(signInError.message)) {
          setUnconfirmedSignIn(true);
          setError("Confirm your email before signing in. Check your inbox or resend the confirmation email.");
          toast.info("Confirm your email first. Your email and password are still here.");
          return;
        }
        setUnconfirmedSignIn(false);
        setError(signInError.message);

        return;
      }
      setUnconfirmedSignIn(false);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      closeRef.current?.focus?.({ preventScroll: true });
      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      setOpen(false);
      setError("");
      setBusy(false);
      setAuthPrompt({ title: "", body: "" });
      toast.success("Signed in!");
      const redirect = postAuthRedirect;
      setPostAuthRedirect("");
      if (redirect) navigate(redirect, { replace: true });
    } catch (err) {
      setError(err?.message || "Login failed.");

    } finally {
      setBusy(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!signInData.email.trim()) {
      toast.error("Enter your email to resend confirmation.");
      return;
    }
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email: signInData.email.trim(),
        options: { emailRedirectTo: EMAIL_CONFIRM_REDIRECT_URL },
      });
      if (resendError) throw resendError;
      toast.success("Confirmation email sent. Check your inbox, then try logging in again.");
    } catch (err) {
      toast.error(err?.message || "Could not resend confirmation email.");
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setMode("signin");
    setError("Business setup now starts from an existing Snows ProAm profile. Sign in first, then link business tools from the setup flow.");
    toast.info("Sign in with your profile first, then link business tools.");
  };

  const regionOptions = useMemo(() => {
    const regions = signup.country === "Ireland" ? IRELAND_REGIONS : signup.country === "United Kingdom" ? UK_REGIONS : [];
    return [{ v: "", l: signup.country ? "Select region" : "Pick country first" }, ...regions.map((region) => ({ v: region, l: region }))];
  }, [signup.country]);

  const value = useMemo(() => ({ openAuthModal, closeAuthModal }), [closeAuthModal, openAuthModal]);

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      {open && (
        <dialog ref={dialogRef} className="site-auth-dialog" aria-labelledby="global-auth-title" onCancel={(event) => { event.preventDefault(); if (!busy) closeAuthModal(); }}>
          <div className="site-auth-card" style={{ width: "min(460px,100%)", maxHeight: "90svh", background: C.bgCard, color: C.text, borderRadius: 18, border: `1px solid ${C.border}`, boxShadow: C.mode === "dark" ? "0 40px 100px rgba(0,0,0,0.70)" : "0 40px 100px rgba(10,29,68,0.20)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div className="site-auth-heading" style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <img src={logo} alt="Snows ProAm" style={{ height: 22, flexShrink: 0 }} />
                <h2 id="global-auth-title" style={{ margin: 0, fontFamily: "'Sora',sans-serif", fontSize: 15, color: C.text }}>{authPrompt.title || "Welcome back"}</h2>
              </div>
              <button ref={closeRef} disabled={busy} type="button" onClick={closeAuthModal} aria-label="Close sign in modal" style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer" }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="site-auth-body" style={{ padding: 20, overflowY: "auto", flex: 1 }}>
              {!authPrompt.body && <p className="site-auth-intro">Sign in to your Snows ProAm account.</p>}
              {authPrompt.body && mode === "signin" ? (
                <div style={{ padding: "11px 12px", borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.textSecondary, fontSize: 13, fontWeight: 750, lineHeight: 1.45, marginBottom: 14 }}>
                  {authPrompt.body}
                </div>
              ) : null}

              {mode === "signin" ? (
                <form onSubmit={handleSignIn} style={{ display: "grid", gap: 12 }}>
                  <Field label="Email" type="email" name="email" value={signInData.email} onChange={(event) => { setSignInData((prev) => ({ ...prev, email: event.target.value })); setSignInErrors((prev) => ({ ...prev, email: false })); setUnconfirmedSignIn(false); }} error={signInErrors.email} errorMsg="Email is required" autoComplete="email" C={C} />
                  <PasswordField label="Password" name="password" value={signInData.password} onChange={(event) => { setSignInData((prev) => ({ ...prev, password: event.target.value })); setSignInErrors((prev) => ({ ...prev, password: false })); setUnconfirmedSignIn(false); }} show={showPassword} toggle={() => setShowPassword((value) => !value)} error={signInErrors.password} errorMsg="Password is required" autoComplete="current-password" C={C} />
                  {error && <div role="alert" style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.24)", color: C.danger, fontSize: 13, fontWeight: 700 }}>{error}</div>}
                  {unconfirmedSignIn && <button type="button" onClick={handleResendConfirmation} style={{ width: "100%", background: "transparent", border: `1px solid ${C.accentBorder}`, color: C.accent, padding: "12px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 900 }}>Resend confirmation email</button>}
                  <button type="submit" disabled={busy} style={{ width: "100%", background: C.accentGrad, border: "none", color: "#fff", padding: "13px", borderRadius: 10, cursor: busy ? "wait" : "pointer", fontSize: 14, fontWeight: 900, fontFamily: "'Sora',sans-serif", opacity: busy ? 0.72 : 1 }}>
                    {busy ? "Signing in…" : "Sign in"}
                  </button>
                  <button type="button" onClick={handleForgotPassword} style={{ border: "none", background: "transparent", color: C.accent, fontSize: 13, fontWeight: 800, cursor: "pointer", padding: 6 }}>Forgot password?</button>
                </form>
              ) : (
                <form onSubmit={handleSignup} style={{ display: "grid", gap: 12 }}>
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 13, fontWeight: 700, color: C.textSecondary }}>Create an institution account for clubs, schools, companies, and organisations.</div>
                  <Field label="Email" type="email" value={signup.email} onChange={(event) => updateSignup("email", event.target.value)} error={signupErrors.email} errorMsg="Enter a valid email" autoComplete="email" C={C} />
                  <PasswordField label="Password" value={signup.password} onChange={(event) => updateSignup("password", event.target.value)} show={showPassword} toggle={() => setShowPassword((value) => !value)} error={signupErrors.password} errorMsg="Use 8+ chars with upper, lower, number, and special character" autoComplete="new-password" C={C} />
                  <PasswordField label="Confirm password" value={signup.confirmPassword} onChange={(event) => updateSignup("confirmPassword", event.target.value)} show={showPassword} toggle={() => setShowPassword((value) => !value)} error={signupErrors.confirmPassword} errorMsg="Passwords must match" autoComplete="new-password" C={C} />
                  <Field label="Institution name" value={signup.businessName} onChange={(event) => updateSignup("businessName", event.target.value)} error={signupErrors.businessName} errorMsg="Required" autoComplete="organization" C={C} />
                  <SelectField label="Institution type" value={signup.instituteType} onChange={(event) => updateSignup("instituteType", event.target.value)} error={signupErrors.instituteType} C={C} options={[{ v: "", l: "Select institution type" }, { v: "School", l: "School" }, { v: "College", l: "College / University" }, { v: "Club", l: "Sports Club" }, { v: "Gym", l: "Gym" }, { v: "Organisation", l: "Organisation" }, { v: "Company", l: "Company" }, { v: "Collective Group", l: "Collective Group" }]} />
                  {["Club", "Organisation", "Company", "Collective Group"].includes(signup.instituteType) && <SelectField label="Organisation size" value={signup.groupSize} onChange={(event) => updateSignup("groupSize", event.target.value)} error={signupErrors.groupSize} C={C} options={[{ v: "", l: "Select organisation size" }, { v: "1-7", l: "1-7 members" }, { v: "8-15", l: "8-15 members" }, { v: "16-30", l: "16-30 members" }, { v: "31-75", l: "31-75 members" }, { v: "76-150", l: "76-150 members" }, { v: "150+", l: "150+ members" }]} />}
                  <SelectField label="Country" value={signup.country} onChange={(event) => updateSignup("country", event.target.value)} error={signupErrors.country} C={C} options={[{ v: "", l: "Select country" }, { v: "Ireland", l: "Ireland" }, { v: "United Kingdom", l: "United Kingdom" }]} />
                  <SelectField label="Region" value={signup.region} onChange={(event) => updateSignup("region", event.target.value)} disabled={!signup.country} error={signupErrors.region} C={C} options={regionOptions} />
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, color: C.textSecondary, fontSize: 13, fontWeight: 700 }}>
                    <input type="checkbox" checked={signup.agreeTerms} onChange={(event) => updateSignup("agreeTerms", event.target.checked)} style={{ width: 17, height: 17, marginTop: 1, accentColor: C.accent }} />
                    <span>I agree to the Terms and Privacy Policy</span>
                  </label>
                  {signupErrors.agreeTerms && <span style={{ fontSize: 12, color: C.danger, fontWeight: 700 }}>You must agree before creating an account.</span>}
                  {error && <div role="alert" style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.24)", color: C.danger, fontSize: 13, fontWeight: 700 }}>{error}</div>}
                  <button type="submit" disabled={busy} style={{ width: "100%", background: C.accentGrad, border: "none", color: "#fff", padding: "13px", borderRadius: 10, cursor: busy ? "wait" : "pointer", fontSize: 14, fontWeight: 900, fontFamily: "'Sora',sans-serif", opacity: busy ? 0.72 : 1 }}>
                    {busy ? "Creating account..." : "Create Institution Account"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </dialog>
      )}
    </AuthModalContext.Provider>
  );
}

export const useAuthModal = () => useContext(AuthModalContext);
